import { useMutation, useQuery } from "@tanstack/react-query";
import React from "react";
import { API } from "../../../API";
import { AgencyId, CaregiverDocumentTypeId, ComplianceFormQuestion } from "../../../messages";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import { assertDefined } from "../../../utils/assertUndefinedable";
import {
  DocumentToUpload,
  flattenDocumentTypeGroupInfo,
  getI9DocumentsToUpload,
} from "../../../utils/complianceUtils";
import { doNothing } from "../../../utils/doNothing";
import ErrorScaffold from "../../ErrorScaffold";
import AppModal from "../../Generic/AppModal/AppModal";
import LoadingScaffold from "../../LoadingScaffold";
import useModal from "../../useModal";
import { withMedflytAgencyId } from "../../withMedflytAgencyId";
import ComplianceFormScreen from "./ComplianceFormScreen";

interface Props {
  api: API;
  medflytAgencyId: AgencyId;
  onRequestGoBack: () => void;
}

function ComplianceI9Activity(props: Props) {
  const modal = useModal();

  const query = useQuery({
    queryKey: ["compliance_i9_form"],
    queryFn: () => props.api.getComplianceI9Form(),
    select: (data) => ({
      form: data.form,
      questionIdToDocumentTypeId: new Map(data.questionOptionIdDocumentTypeIdEntries),
    }),
  });

  const itemsQuery = useQuery({
    queryKey: ["compliance_items", props.medflytAgencyId],
    queryFn: () => props.api.getComplianceItems(props.medflytAgencyId),
    select: (data) => {
      const i9Document = assertDefined(
        data.items.find((item) => item.documentType.adhocType === "i-9")
      );

      const allI9DocumentTypes = i9Document.documentType.children.flatMap(
        flattenDocumentTypeGroupInfo
      );

      return new Map(
        allI9DocumentTypes.map((type) => [
          type.id,
          {
            id: type.id,
            name: type.name,
            isPassport: type.isPassport,
          },
        ])
      );
    },
  });

  const mutateUploadDocument = useMutation(
    ({ document, base64 }: { document: DocumentToUpload; base64: string }) => {
      return document.isPassport
        ? props.api.uploadMedflytPassportDocument({
            base64: base64,
            categoryId: CaregiverDocumentTypeId.unwrap(document.id),
            documentTypeId: document.id,
            documentName: document.name,
            categoryName: document.name,
            expiryDate: null,
          })
        : props.api.uploadMedflytPassportAgencyDocument(props.medflytAgencyId, document.id, {
            base64: base64,
            expiryDate: null,
          });
    }
  );

  const handleSubmit = (questions: ComplianceFormQuestion[]) => {
    if (
      query.isError ||
      query.isLoading ||
      itemsQuery.isError ||
      itemsQuery.isLoading ||
      questions.length === 0
    ) {
      return;
    }

    const lastQuestion = questions[questions.length - 1];
    const isSuccessfulPath = lastQuestion.type === "document";

    if (!isSuccessfulPath) {
      props.onRequestGoBack();
      return;
    }

    const documentsToUpload = getI9DocumentsToUpload({
      questions,
      questionIdToDocumentTypeId: query.data.questionIdToDocumentTypeId,
      documentIdToUploadDocument: itemsQuery.data,
    });

    Promise.all(
      documentsToUpload.map((documentToUpload) =>
        mutateUploadDocument.mutateAsync(documentToUpload)
      )
    )
      .then(() => {
        itemsQuery.refetch();

        modal.setModalAsSuccess({
          title: translate(STRINGS.SUCCESS),
          message: translate(STRINGS.COMPLIANCE_FORM_SUBMITTED_SUCCESSFULLY),
          actions: [
            {
              key: "ok",
              variant: "filled",
              size: "small",
              children: translate(STRINGS.OK),
              onClick: () => {
                modal.setModalAsClosed();
                props.onRequestGoBack();
              },
            },
          ],
        });
      })
      .catch(() => {
        modal.setModalAsError({
          title: translate(STRINGS.ERROR),
          message: translate(STRINGS.SOMETHING_WENT_WRONG),
        });
      });
  };

  if (query.isError || itemsQuery.isError) {
    return (
      <ErrorScaffold
        title="I-9"
        message={translate(STRINGS.SOMETHING_WENT_WRONG)}
        onClickRetry={query.refetch}
        onClickGoBack={props.onRequestGoBack}
      />
    );
  }

  if (query.isLoading || itemsQuery.isLoading) {
    return <LoadingScaffold title="I-9" onClickGoBack={doNothing} />;
  }

  return (
    <>
      <ComplianceFormScreen
        formDocument={query.data.form}
        isSubmitting={mutateUploadDocument.isLoading}
        onSubmit={handleSubmit}
        onClickGoBack={props.onRequestGoBack}
        onError={modal.setModalAsError}
      />
      <AppModal {...modal.modalProps} />
    </>
  );
}

export default withMedflytAgencyId(ComplianceI9Activity);
